<template>
  <div class="kst-table" :class="rootClass">
    <kst-skeleton :active="loading"/>
    <kst-failure list-mode :items="failure"/>

    <template v-if="!failure">
      <template v-if="!loading">
        <b-table focusable hoverable striped
          ref="myTable"
          :checkable="checkable"
          :checked-rows="checkedRows"
          :class="theClass"
          :custom-is-checked="customIsChecked"
          :data="data"
          :default-sort="sort"
          :default-sort-direction="sortDirection"
          :detail-key="detailKey"
          :detailed="detailed"
          :header-checkable="headerCheckable"
          :opened-detailed="openedDetails"
          :paginated="thePaginated"
          :per-page="perPage"
          :selected="selected"
          :show-detail-icon="detailed"
          :sort-icon="sortIcon"
          :sort-icon-size="sortIconSize"
          @check="handleCheck"
          @page-change="handlePageChange"
          @select="handleSelect"
          @sort="handleSort"
        >
          <template v-if="!sequenceOff">
            <b-table-column
              v-slot="{ index }"
              cell-class="ks-col-sequence"
              width="10"
              :label="theSequenceLabel"
            >
              <div :class="viewMode ? '' : 'k-control-py'">
                {{ ((currentPage - 1) * perPage) + index + 1 }}
              </div>
            </b-table-column>
          </template>

          <slot/>

          <template v-if="action">
            <b-table-column :cell-class="theActionCellClass" width="10">
              <template #header>
                <kst-header-blank />
              </template>
              <template #default="props">
                <slot name="action"
                  :colindex="props.colindex"
                  :column="props.column"
                  :index="props.index"
                  :row="props.row"
                  :toggleDetails="props.toggleDetails"
                />
              </template>
            </b-table-column>
          </template>

          <template v-if="detailed" #detail="props">
            <slot name="detail"
              :colindex="props.colindex"
              :column="props.column"
              :index="props.index"
              :row="props.row"
              :toggleDetails="props.toggleDetails"
            />
          </template>

          <template #empty>
            <slot name="empty">
              <kst-table-empty>
                <slot name="empty-body"/>
              </kst-table-empty>
            </slot>
          </template>

          <template #footer>
            <slot name="footer"/>
          </template>
        </b-table>
      </template>
    </template>
  </div>
</template>

<script>
import AppCatalog from "../../../services/App/AppCatalog.js";

// intended widths already declared by the ks-col-* cell classes (kst.scss)
// fallback used as the default (minimum) column width for resizable tables
const RESIZE_COL_WIDTHS = {
  "ks-col-nopad": 24,
  "ks-col-sequence": 32,
  "ks-col-action": 60,
  "ks-col-action-view-padding": 60,
  "ks-col-packaging": 150,
  "ks-col-qty": 100,
  "ks-col-price": 140,
  "ks-col-price-button": 170,
  "ks-col-disc-type": 115,
  "ks-col-disc-value": 135,
  "ks-col-specialprice-button": 175,
  "ks-col-specialpricename": 200,
  "ks-col-subtotal": 135
};

export default {
  props: {
    action: Boolean,
    autoToggleDetails: Boolean, // MUST have detailKey, detailed
    checkable: Boolean,
    checkedRows: Array,
    customIsChecked: {
      type: Function,
      default: null
    },
    data: Array,
    detailKey: String,
    detailed: Boolean,
    failure: [Array, String],
    headerCheckable: {
      type: Boolean,
      default: true
    },
    loading: Boolean,
    paginated: Boolean,
    perPage: {
      type: Number,
      default: AppCatalog.Table.PerPage
    },
    selected: Object,
    sequenceOff: Boolean,
    sort: String, // column name
    sortDirection: String, // see Table.Sort enum in "@/services/App/App_Buefy.js",
    // mode: ONLY select 1 (one)
    inputMode: Boolean,
    viewMode: Boolean,
    // enable expandable columns (drag header to resize, horizontal scroll)
    resizable: Boolean
  },
  data: () => ({
    currentPage: 1,
    openedDetails: [],
    sortIcon: AppCatalog.Table.SortIcon,
    sortIconSize: AppCatalog.Table.SortIconSize,
    // resize state
    resizeCols: [],
    resizeHandles: [],
    resizeWidths: [],
    resizeFromHeaderFallback: false,
    resizing: null
  }),
  watch: {
    data() {
      this.currentPage = 1;
    }
  },
  computed: {
    rootClass() {
      return this.resizable ? "ks-table-resizable" : undefined;
    },
    theActionCellClass() {
      return this.viewMode
        ? "ks-col-action ks-col-action-view-padding"
        : "ks-col-action";
    },
    theClass() {
      return this.getClass();
    },
    thePaginated() {
      return this.getPaginated();
    },
    theSequenceLabel() {
      return AppCatalog.Info.Sequence;
    }
  },
  created() {
    this.init();
  },
  mounted() {
    if (this.resizable) {
      this.$nextTick(() => this.syncResize());
    }
  },
  updated() {
    if (this.resizable) {
      this.syncResize();
    }
  },
  beforeDestroy() {
    if (this.resizable) {
      this.destroyResize();
    }
  },
  methods: {
    init() {
      this.setAutoToggleDetails(this.checkedRows);
    },

    handleCheck(checkedList, row) {
      this.$emit("update:checked-rows", checkedList);
      this.$emit(this.$kst.Enum.Event.Check, checkedList, row);

      this.updateAutoToggleDetails(checkedList, row);
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    handleSelect(row) {
      this.$emit("update:selected", row);
    },
    handleSort(field, order) {
      this.$emit(this.$kst.Enum.Event.Sort, field, order);
    },

    getClass() {
      if (this.inputMode) {
        return "ks-table-input";
      }
      else if (this.viewMode) {
        return "ks-table-view";
      }

      return undefined;
    },
    getPaginated() {
      if (this.data === undefined || this.data === null) {
        return false;
      }
      else if (this.data.length === 0) {
        return false;
      }

      return this.paginated;
    },

    /*** resize (resizable) ***/

    syncResize() {
      const table = this.getResizeTable();
      if (!table) {
        return;
      }

      const ths = table.querySelectorAll("thead tr:first-child th");
      if (this.resizeCols.length !== ths.length) {
        this.rebuildResize(table, ths);
      }
      // if widths were guessed from header (empty table), recompute once real rows appear
      else if (this.resizeFromHeaderFallback && table.querySelector("tbody tr:not(.is-empty)")) {
        this.rebuildResize(table, ths);
      }
    },

    getResizeTable() {
      if (!this.resizable || !this.$refs.myTable || !this.$refs.myTable.$el) {
        return null;
      }

      return this.$refs.myTable.$el.querySelector(".table-wrapper .table");
    },

    rebuildResize(table, ths) {
      this.destroyResize();

      const colgroup = document.createElement("colgroup");
      this.resizeFromHeaderFallback = false;
      this.resizeWidths = [];

      for (let i = 0; i < ths.length; i++) {
        const width = this.getDefaultWidth(table, ths[i], i);
        const col = document.createElement("col");
        col.style.width = `${width}px`;
        colgroup.appendChild(col);
        this.resizeWidths.push(width);
        this.resizeCols.push(col);
      }
      table.insertBefore(colgroup, table.firstChild);

      // resize handle on every column except the last (actions column)
      for (let i = 0; i < ths.length; i++) {
        if (i === ths.length - 1) {
          continue;
        }

        const handle = document.createElement("div");
        handle.className = "ks-resize-handle";
        handle.title = "Drag to resize column";
        handle.addEventListener("mousedown", (e) => this.startResize(e, i));
        ths[i].appendChild(handle);
        this.resizeHandles.push(handle);
      }
    },

    getDefaultWidth(table, th, index) {
      // 1) base: the td class (ks-col-*) already carries the intended width
      const rows = table.querySelectorAll("tbody tr:not(.is-empty):not(.detail):not(.table-footer)");
      const tds = rows.length > 0 ? rows[0].querySelectorAll("td") : [];
      const td = tds[index];
      const tdClass = td ? String(td.className || "") : "";
      let width = null;
      if (td) {
        for (const c of tdClass.split(" ")) {
          if (RESIZE_COL_WIDTHS[c]) {
            width = RESIZE_COL_WIDTHS[c];
            break;
          }
        }
      }

      // 2) no class (e.g. Name): derive from header text length
      if (width === null) {
        const text = (th.textContent || "").trim();
        width = Math.min(240, Math.max(90, (text.length * 7.5) + 40));
        // empty table: recompute once real rows appear
        if (td === undefined) {
          this.resizeFromHeaderFallback = true;
        }
      }

      // 3) content-aware: widen to fit the longest visible content in this column,
      //    so fixed table-layout doesn't squeeze/wrap existing values.
      //    NOTE: skipped for action/sequence columns — their "text" includes the
      //    hidden tooltip copy, which must NOT resize the column.
      let maxNeed = 0;
      let hasIcon = false;
      const isActionCol = tdClass.includes("ks-col-action");
      const isSequenceCol = tdClass.includes("ks-col-sequence");
      if (!isActionCol && !isSequenceCol) {
        const rowCount = Math.min(rows.length, 30);
        for (let r = 0; r < rowCount; r++) {
          const cell = rows[r].querySelectorAll("td")[index];
          if (!cell) {
            continue;
          }

          let text = "";
          const select = cell.querySelector("select");
          if (select) {
            const opt = select.options[select.selectedIndex];
            text = opt ? opt.textContent : "";
          }
          else {
            // merged cells may hold a hidden input (validation) — measure the visible one
            const visibleInput = Array.from(cell.querySelectorAll("input"))
              .find(i => i.getBoundingClientRect().width > 0);
            if (visibleInput) {
              text = visibleInput.value || visibleInput.placeholder || "";
            }
            else {
              // text/output cells: measure only the field, ignore tooltip content
              const field = cell.querySelector(".field");
              text = ((field || cell).textContent || "").trim();
            }
          }

          const len = String(text).length;
          if (len > maxNeed) {
            maxNeed = len;
          }
          // cells that also host an icon next to the input need a bit more room
          if (!hasIcon && cell.querySelector(".kst-tooltip")) {
            hasIcon = true;
          }
        }
      }
      if (maxNeed > 0) {
        let contentNeed = Math.max(60, Math.min(320, (maxNeed * 7.5) + 24));
        if (hasIcon) {
          contentNeed += 20;
        }
        width = Math.max(width, contentNeed);
      }

      return Math.round(Math.min(320, Math.max(48, width)));
    },

    startResize(event, index) {
      this.destroyResizeListeners();
      this.resizing = {
        index: index,
        startX: event.clientX,
        startWidth: this.resizeWidths[index]
      };

      window.addEventListener("mousemove", this.handleResizeMove);
      window.addEventListener("mouseup", this.handleResizeEnd);
      document.body.classList.add("ks-resizing");
    },
    handleResizeMove(event) {
      if (!this.resizing) {
        return;
      }

      const diff = event.clientX - this.resizing.startX;
      const width = Math.max(60, Math.min(600, this.resizing.startWidth + diff));
      this.resizeWidths[this.resizing.index] = width;

      const col = this.resizeCols[this.resizing.index];
      if (col) {
        col.style.width = `${width}px`;
      }
    },
    handleResizeEnd() {
      this.destroyResizeListeners();
      this.resizing = null;
      document.body.classList.remove("ks-resizing");
    },
    destroyResizeListeners() {
      window.removeEventListener("mousemove", this.handleResizeMove);
      window.removeEventListener("mouseup", this.handleResizeEnd);
    },
    destroyResize() {
      this.destroyResizeListeners();
      this.resizing = null;
      document.body.classList.remove("ks-resizing");

      // remove handles
      for (const handle of this.resizeHandles) {
        if (handle && handle.parentNode) {
          handle.parentNode.removeChild(handle);
        }
      }
      this.resizeHandles = [];

      // remove colgroup
      const group = this.resizeCols[0] ? this.resizeCols[0].parentNode : null;
      if (group && group.parentNode) {
        group.parentNode.removeChild(group);
      }
      this.resizeCols = [];
      this.resizeWidths = [];
      this.resizeFromHeaderFallback = false;
    },

    setAutoToggleDetails(checkedList) {
      // validation
      if (!this.hasAutoToggleDetails()) {
        return;
      }

      this.openedDetails = [];

      for (const row of checkedList) {
        this.openedDetails.push(row[this.detailKey]);
      }
    },
    updateAutoToggleDetails(checkedList, row) {
      // if: check/uncheck all
      if (row === undefined) {
        this.setAutoToggleDetails(checkedList);
      }
      // else: check/uncheck row
      else {
        // validation
        if (!this.hasAutoToggleDetails()) {
          return;
        }

        if (checkedList.includes(row)) {
          const rowId = row[this.detailKey];
          if (!this.openedDetails.includes(rowId)) {
            this.$refs.myTable.openDetailRow(row);
          }
        }
        else {
          this.$refs.myTable.closeDetailRow(row);
        }
      }
    },
    hasAutoToggleDetails() {
      if (!this.autoToggleDetails) {
        return false;
      }
      if (this.detailKey === undefined || this.detailKey === null) {
        return false;
      }

      return true;
    }
  }
}
</script>